/* =========================================
   Cart Drawer v3.0 — Piggyback Sync (Phase A)

   EVOLUTION FROM v2.1:
   Previous architecture triggered 2–3 extra network round-trips per
   gift sync cycle (fresh config GET + fresh cart GET + post-mutation
   refreshSections GET). This version piggybacks on data already
   returned by Dawn's cart mutations and by Shopify's section rendering,
   cutting the typical sync from ~2s to ~800ms without weakening the
   server-authoritative model for gift eligibility.

   CORE INVARIANTS (UNCHANGED FROM v2.1):
   - Gift rules are evaluated in Liquid. JS only executes what Liquid
     says. Trigger-tag logic never moves client-side because /cart.js
     does not expose product tags.
   - Checkout stays locked until the cart state matches the rules.
   - Max 3 sync attempts to prevent loops; gift qty capped to maxQty.

   WHAT CHANGED:
   1. runGiftSync() now accepts optional { preloadedConfig, preloadedCart }.
      Callers that already hold fresh HTML (from a cart mutation response)
      pass it in instead of forcing another network round-trip.
   2. cartPost() accepts `sections` in the body. Add/update mutations
      return fresh section HTML, from which we extract the next
      triggerActive without a separate GET.
   3. Debounce lowered 800ms → 150ms. The old value was defensive
     padding; 150ms is enough to coalesce rapid clicks and gives
     Shopify's cart API time to settle in practice.
   4. applySectionsFromMap() replaces the post-mutation refreshSections()
      call — the HTML is already in memory after the mutation resolves.
========================================= */

"use strict";

/* =========================================
   SECTION: Logger
========================================= */
const CartLogger = {
  _p: '[Cart]',
  info(...a) { console.log(this._p, ...a); },
  warn(...a) { console.warn(this._p, ...a); },
  error(...a) { console.error(this._p, ...a); },
  debug(...a) { if (window.CART_DEBUG) console.log(this._p, '[DEBUG]', ...a); }
};
window.CartLogger = CartLogger;

/* =========================================
   SECTION: CartDrawer
========================================= */
class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this._giftSyncLock = false;
    this._syncAttempts = 0;
    this._MAX_SYNC_ATTEMPTS = 3;

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.setupCrossTabSync();

    /* CRITICAL: Lock checkout on ANY cart interaction instantly.
       Uses event delegation — catches clicks on quantity buttons,
       remove buttons, and input changes before AJAX fires. */
    this.addEventListener('click', (e) => {
      if (e.target.closest('cart-remove-button') ||
          e.target.closest('.quantity__button') ||
          e.target.closest('button[name="minus"]') ||
          e.target.closest('button[name="plus"]')) {
        this.setCheckoutLock(true);
        CartLogger.debug('Checkout locked: user interaction detected');
      }
    });

    /*
      SAMPLES CHECKBOX PERSISTENCE.

      The input's name="attributes[Request Samples]" only submits with the
      cart form at checkout — until then the tick lives in DOM only. Any
      server re-render (discount apply/clear → refreshSections(), gift sync,
      qty updates) rebuilds the drawer from cart.attributes, silently wiping
      the visual state. Fix: persist to /cart/update.js on every change,
      mirroring Dawn's cart-note pattern.

      - Delegated on <cart-drawer> (never replaced by innerHTML swaps),
        so the listener survives all section re-renders.
      - Requests are CHAINED on this._samplesSync: rapid toggle clicks
        resolve in order, last write wins server-side too.
      - refreshSections() awaits this promise (see below) so a re-render
        can never race ahead of the attribute write.
    */
    this.addEventListener('change', (e) => {
      const check = e.target.closest('.eisNova-samples__check');
      if (!check) return;
      const value = check.checked ? 'Yes' : '';
      CartLogger.debug('Samples attribute →', value || '(cleared)');
      this._samplesSync = (this._samplesSync || Promise.resolve())
        .catch(() => {}) /* a failed previous write must not block the chain */
        .then(() => this.cartPost('/cart/update.js', {
          attributes: { 'Request Samples': value }
        }))
        .then(() => this.notifyCartChanged?.());
    });

    /* Initial measurement after the drawer is upgraded — ensures cross-panel
       sits flush with footer divider on first drawer open, before any user
       interaction. requestAnimationFrame inside syncCrossPanelHeight()
       guarantees layout has settled before getBoundingClientRect runs. */
    this.syncCrossPanelHeight();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.refreshAndOpen();
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.refreshAndOpen();
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    CartLogger.debug('open');

    const note = this.querySelector('[id^="Details-"] summary');
    if (note && !note.hasAttribute('role')) this.setSummaryAccessibility(note);

    setTimeout(() => { this.classList.add('animate', 'active'); });

    this.addEventListener('transitionend', () => {
      const container = this.classList.contains('is-empty')
        ? this.querySelector('.drawer__inner-empty')
        : document.getElementById('CartDrawer');
      const focusEl = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
      if (container) trapFocus(container, focusEl);
    }, { once: true });

    document.body.classList.add('overflow-hidden');

    /* Mobile-only hard scroll lock. On iOS Safari overflow:hidden on <body>
       doesn't stop the page rubber-banding (the real scroller is <html>), so on
       small viewports we pin <body> with position:fixed and stash the scroll
       offset to restore on close. Desktop is left scrollable on purpose. */
    if (window.matchMedia('(max-width: 520px)').matches) {
      this._lockedScrollY = window.scrollY;
      document.body.style.top = `-${this._lockedScrollY}px`;
      document.body.classList.add('eisNova-locked');
    }

    document.dispatchEvent(new CustomEvent('cart:opened'));
  }

  close() {
    CartLogger.debug('close');
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');

    /* Release the mobile lock and restore the pre-open scroll position. */
    if (document.body.classList.contains('eisNova-locked')) {
      document.body.classList.remove('eisNova-locked');
      document.body.style.top = '';
      window.scrollTo(0, this._lockedScrollY || 0);
    }

    document.dispatchEvent(new CustomEvent('cart:closed'));
  }

/* =========================================
     SECTION: Cross-tab cart sync

     Two tabs open → a mutation (or gift drop) in tab A leaves tab B stale.
     Reconcile on two signals:
       1. BroadcastChannel — instant. Any tab whose cart settles pings the
          channel; every OTHER open-drawer tab repaints from server state.
          Receiver does NOT re-run gift-sync (the source tab already did, so
          the server is correct) — repaint only, which avoids two tabs racing
          to add/remove the same gift.
       2. visibilitychange — fallback for changes that never hit the channel
          (e.g. a PDP add-to-cart in another tab). On refocus, if the drawer
          is open, repaint AND self-heal with a foreground gift sync.

     SEND side rides on `cart:gift-sync-end`: it fires after qty/remove, gift
     add/remove, discount apply/clear, overlay add and loyalty redeem — every
     drawer-driven mutation funnels through runGiftSync — so one listener
     covers them all. BroadcastChannel never echoes to the sender, so there is
     no self-trigger loop; receivers only repaint, so propagation is bounded.
  ========================================= */
  setupCrossTabSync() {
    this._isDrawerOpen = () => this.classList.contains('active');

    /* RECEIVE — instant cross-tab repaint. */
    if ('BroadcastChannel' in window) {
      this._cartChannel = new BroadcastChannel('eisnova-cart');
      this._cartChannel.onmessage = (e) => {
        if (e.data?.type !== 'cart-changed') return;
        CartLogger.debug('Cross-tab: cart changed elsewhere — refreshing bubble + drawer');
        /* Refresh regardless of open state: refreshSections() re-renders BOTH
           cart-drawer AND cart-icon-bubble, so the header count updates even
           while the drawer is closed. Repaint only — no gift mutation here. */
        this.refreshSections().catch(err => CartLogger.warn('Cross-tab refresh failed:', err));
      };
    }

    /* SEND — one signal covers every drawer-driven mutation. */
    document.addEventListener('cart:gift-sync-end', () => this.broadcastCartChange());

    /* RECEIVE (fallback) — reconcile on refocus, foreground self-heal. */
    this._onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      CartLogger.debug('Tab visible again — reconciling cart');
      /* Always refresh (updates the header bubble too). Self-heal with a gift
         sync ONLY when the drawer is open — never mutate the cart from a
         passive / closed background tab. */
      this.refreshSections()
        .then((map) => {
          if (!this._isDrawerOpen()) return;
          const html = map?.['cart-drawer'];
          if (html) this.runGiftSync({ preloadedConfig: this.extractConfigFromSectionHTML(html) });
        })
        .catch(err => CartLogger.warn('Visibility reconcile failed:', err));
    };
    document.addEventListener('visibilitychange', this._onVisible);
  }

  /* Tell other tabs this tab's cart changed. Sender never receives its own
     message, so there is no echo. */
  broadcastCartChange() {
    try { this._cartChannel?.postMessage({ type: 'cart-changed', ts: Date.now() }); }
    catch (_) { /* channel closed / unsupported — non-fatal */ }
  }

  disconnectedCallback() {
    document.removeEventListener('visibilitychange', this._onVisible);
    this._cartChannel?.close();
  }


  /* =========================================
     SECTION: Section Rendering
  ========================================= */

  /**
   * Main entry point when the cart icon is clicked.
   * No cart mutation happened, so we must fetch fresh HTML once —
   * but we piggyback the gift config extraction on that same HTML
   * to avoid the old two-GET pattern.
   */
  async refreshAndOpen() {
    CartLogger.debug('refreshAndOpen');
    this.setCheckoutLock(true);

    try {
      const sectionsMap = await this.refreshSections();
      this.open();

      /* Extract fresh config from the HTML we already fetched
         instead of making a second round-trip in fetchFreshGiftConfig(). */
      const drawerHTML = sectionsMap?.['cart-drawer'];
      const preloadedConfig = drawerHTML
        ? this.extractConfigFromSectionHTML(drawerHTML)
        : null;

      await this.runGiftSync({ preloadedConfig });
    } catch (err) {
      CartLogger.error('refreshAndOpen failed:', err);
      this.open();
      this.setCheckoutLock(false);
    }
  }

  renderContents(parsedState) {
    CartLogger.debug('renderContents', { id: parsedState.id });
    this.querySelector('.drawer__inner')?.classList.remove('is-empty');
    this.productId = parsedState.id;

    this.getSectionsToRender().forEach(section => {
      const el = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      if (el && parsedState.sections?.[section.id]) {
        el.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
      }
    });

    this.rebindOverlay();

    setTimeout(async () => {
      this.open();

      /* Piggyback: parsedState already contains the fresh section HTML
         we'd otherwise re-fetch in fetchFreshGiftConfig(). */
      const drawerHTML = parsedState.sections?.['cart-drawer'];
      const preloadedConfig = drawerHTML
        ? this.extractConfigFromSectionHTML(drawerHTML)
        : null;

      await this.runGiftSync({ preloadedConfig });
    });
  }

  /**
   * Fetches fresh section HTML via the ?sections= endpoint and applies it.
   * Returns the sections map so callers can extract gift config without
   * a second round-trip.
   */
  async refreshSections() {
    /* If a samples-attribute write is in flight (user ticked the box and
       immediately applied a promo code), wait for it — otherwise the
       server renders the drawer from the PRE-write cart state and the
       checkbox visually resets. */
    if (this._samplesSync) {
      try { await this._samplesSync; } catch (_) { /* render regardless */ }
    }
    const ids = this.getSectionsToRender().map(s => s.id);
    const res = await fetch(`${window.location.pathname}?sections=${ids.join(',')}`);
    const sections = await res.json();
    this.applySectionsFromMap(sections);
    return sections;
  }

  /**
   * Applies a sections map (typically from a cart mutation response or
   * from the ?sections= endpoint) directly to the DOM.
   * This is the zero-network alternative to refreshSections() whenever
   * the HTML is already in memory.
   */
  applySectionsFromMap(sectionsMap) {
    if (!sectionsMap) return;

    /* Sync the outer <cart-drawer> .is-empty class from the freshly rendered
       section HTML BEFORE swapping innerHTML. Without this, going from empty
       to non-empty via the overlay leaves the stale is-empty class on, and
       Dawn's `cart-drawer.is-empty .drawer__inner { display: grid; grid-template-rows:1fr }`
       breaks layout (items collapse, header/shipping/footer pile centered).

       cart.js:updateQuantity already handles this for Dawn's +/- path; this
       branch covers all add/remove flows that go through applySectionsFromMap
       directly (overlay submit, _directAdd, runGiftSync responses, etc.). */
    const drawerSectionHTML = sectionsMap['cart-drawer'];
    if (drawerSectionHTML) {
      const parsed = new DOMParser().parseFromString(drawerSectionHTML, 'text/html');
      const freshCartDrawer = parsed.querySelector('cart-drawer');
      if (freshCartDrawer) {
        this.classList.toggle('is-empty', freshCartDrawer.classList.contains('is-empty'));
      }
    }

    this.getSectionsToRender().forEach(section => {
      const html = sectionsMap[section.id];
      if (!html) return;
      const el = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      if (el) {
        el.innerHTML = this.getSectionInnerHTML(html, section.selector);
      }
    });
    this.rebindOverlay();
    this.syncCrossPanelHeight();
  }

  /*
    Measures the live footer block height and stores it in --eisNova-footer-h.
    The cross-sell side panel uses this value as its `bottom: var(--eisNova-footer-h)`
    so it ends precisely at the divider line above SUBTOTAL — regardless of which
    optional blocks are present (cart note, promo code, max-cart warning, loyalty
    text, expanded toggles).

    Called after every section re-render (item add/remove/qty change) because
    footer height fluctuates with cart state.
  */
  syncCrossPanelHeight() {
    requestAnimationFrame(() => {
      const footer = this.querySelector('.eisNova-footer');
      if (!footer) return;
      const h = footer.getBoundingClientRect().height;
      if (h > 0) {
        document.documentElement.style.setProperty('--eisNova-footer-h', `${Math.round(h)}px`);
      }
    });
  }

  rebindOverlay() {
    this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector)?.innerHTML || '';
  }

  getSectionsToRender() {
    return [
      { id: 'cart-drawer', selector: '#CartDrawer' },
      { id: 'cart-icon-bubble' }
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) { this.activeElement = element; }

  setSummaryAccessibility(el) {
    el.setAttribute('role', 'button');
    el.setAttribute('aria-expanded', 'false');
    if (el.nextElementSibling?.id) el.setAttribute('aria-controls', el.nextElementSibling.id);
    el.addEventListener('click', e => {
      e.currentTarget.setAttribute('aria-expanded',
        !e.currentTarget.closest('details').hasAttribute('open'));
    });
    el.parentElement?.addEventListener('keyup', onKeyUpEscape);
  }

  /* =========================================
     SECTION: Gift Sync Controller v3 (Piggyback)

     ALGORITHM:
     1. Lock checkout immediately.
     2. Obtain fresh config + cart — either from preloaded payload
        (caller had them from a mutation response) or via fetch.
     3. Compute required actions (add / remove / cap).
     4. Batch all adds into ONE /cart/add.js call, all removes/caps
        into ONE /cart/update.js call, running them in parallel.
     5. Pass `sections` in the mutation body so the response carries
        fresh section HTML — apply it directly (no follow-up GET).
     6. Extract the next iteration's config from that same HTML.
     7. Loop until no more actions (or MAX_SYNC_ATTEMPTS hit).
     8. Unlock checkout only when the state matches all rules.

     ABUSE PREVENTION (UNCHANGED):
     - MAX_SYNC_ATTEMPTS = 3 bounds worst-case iterations.
     - Checkout stays locked if sync throws before clean state.
     - Gift quantities are capped to maxQty server-side via /cart/update.
  ========================================= */

  async runGiftSync({ preloadedConfig = null, preloadedCart = null } = {}) {
    if (this._giftSyncLock) {
      CartLogger.debug('Gift sync: skipped (lock active)');
      return;
    }

    this._giftSyncLock = true;
    this._syncAttempts = 0;
    this.setCheckoutLock(true);
    document.dispatchEvent(new CustomEvent('cart:gift-sync-start'));
    CartLogger.debug('=== GIFT SYNC START ===');

    try {
      let isClean = false;
      let freshConfig = preloadedConfig;
      let cart = preloadedCart;

      while (!isClean && this._syncAttempts < this._MAX_SYNC_ATTEMPTS) {
        this._syncAttempts++;
        CartLogger.debug(`Gift sync: attempt ${this._syncAttempts}/${this._MAX_SYNC_ATTEMPTS}`);

        /* Only fetch what we don't already have.
           On attempt 1 after a cart mutation, both are preloaded → zero GETs.
           On attempt 2+, or after refreshAndOpen(), we fetch only the missing half. */
        if (!freshConfig || !cart) {
          const [c, k] = await Promise.all([
            freshConfig ? Promise.resolve(freshConfig) : this.fetchFreshGiftConfig(),
            cart ? Promise.resolve(cart) : this.fetchCart()
          ]);
          freshConfig = c;
          cart = k;
        }

        if (!freshConfig || freshConfig.length === 0) {
          CartLogger.debug('No gift rules configured — sync complete');
          isClean = true;
          break;
        }

        CartLogger.debug('Cart state:', {
          items: cart.item_count,
          total: cart.total_price
        });

        const actions = this.calculateGiftActions(freshConfig, cart);

        if (actions.length === 0) {
          CartLogger.debug('Gift sync: cart is clean, no actions needed');
          isClean = true;
          break;
        }

        CartLogger.info(`Gift sync: executing ${actions.length} action(s)`);

        const toAdd = actions.filter(a => a.type === 'add');
        const toRemove = actions.filter(a => a.type === 'remove');
        const toCap = actions.filter(a => a.type === 'cap');

        const sectionIds = this.getSectionsToRender().map(s => s.id);
        const mutationPromises = [];
        let addResponse = null;
        let updateResponse = null;

        /* Request fresh section HTML inside the mutation responses so we
           don't need a separate GET afterwards. */
        if (toAdd.length > 0) {
          const items = toAdd.map(a => ({ id: a.variantId, quantity: a.quantity }));
          mutationPromises.push(
            this.cartPost('/cart/add.js', { items, sections: sectionIds })
              .then(r => {
                addResponse = r;
                CartLogger.info('Gift sync: ADDED', items.length, 'gift(s)');
              })
          );
        }

        if (toRemove.length > 0 || toCap.length > 0) {
          const updates = {};
          toRemove.forEach(a => { updates[a.variantId] = 0; });
          toCap.forEach(a => { updates[a.variantId] = a.quantity; });
          mutationPromises.push(
            this.cartPost('/cart/update.js', { updates, sections: sectionIds })
              .then(r => {
                updateResponse = r;
                CartLogger.info('Gift sync: REMOVED/CAPPED', Object.keys(updates).length, 'gift(s)');
              })
          );
        }

        await Promise.all(mutationPromises);

        /* Prefer /cart/update.js response because it always carries the
           full cart shape (items, total_price, item_count).
           /cart/add.js carries sections but NOT the full cart state —
           so if we only ran add, we'll need a /cart.js fetch to reconcile. */
        const authoritativeResponse = updateResponse || addResponse;
        const sectionsMap = authoritativeResponse?.sections;

        if (sectionsMap?.['cart-drawer']) {
          this.applySectionsFromMap(sectionsMap);
          freshConfig = this.extractConfigFromSectionHTML(sectionsMap['cart-drawer']);
        } else {
          /* Defensive fallback: sections missing for some reason. */
          CartLogger.warn('Mutation response missing sections, falling back to GET');
          freshConfig = null;
        }

        /* Detect full-cart response shape without hardcoding Shopify internals. */
        const hasFullCart = authoritativeResponse
          && Array.isArray(authoritativeResponse.items)
          && typeof authoritativeResponse.total_price === 'number';
        cart = hasFullCart ? authoritativeResponse : null;
      }

      if (!isClean) {
        CartLogger.warn('Gift sync: max attempts reached');
      }

      CartLogger.debug('=== GIFT SYNC END ===');
    } catch (err) {
      CartLogger.error('Gift sync failed:', err);
    } finally {
      this._giftSyncLock = false;
      this.setCheckoutLock(false);
      document.dispatchEvent(new CustomEvent('cart:gift-sync-end'));
      /* Fire-and-forget AFTER the sync lock is released — enforcement may
         itself trigger a nested (idempotent) sync via clearDiscount(). */
      this._enforceLoyaltyExclusivity().catch(err =>
        CartLogger.error('Loyalty exclusivity enforcement failed:', err));
    }
  }

  /**
   * Fallback fetcher for when no preloaded HTML is available.
   * Kept public because cart-drawer-tests.js asserts its existence.
   */
  async fetchFreshGiftConfig() {
    try {
      const res = await fetch(`${window.location.pathname}?section_id=cart-drawer`);
      const html = await res.text();
      return this.extractConfigFromSectionHTML(html);
    } catch (err) {
      CartLogger.error('Failed to fetch fresh gift config:', err);
      return null;
    }
  }

  /**
   * Parses the inline gift config script out of any section HTML string.
   * Same script tag is present whether the HTML came from ?section_id=,
   * from ?sections=, or from a cart mutation's sections map — so callers
   * can share this one parser and the server remains the single source
   * of truth for triggerActive evaluation.
   */
  extractConfigFromSectionHTML(html) {
    try {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const el = doc.querySelector('#CartDrawer-GiftConfig');
      if (!el) {
        CartLogger.debug('No gift config in section HTML');
        return null;
      }
      const config = JSON.parse(el.textContent);
      CartLogger.debug('Extracted gift config:', JSON.stringify(config));
      return config;
    } catch (err) {
      CartLogger.error('Failed to extract gift config:', err);
      return null;
    }
  }

  /**
   * Pure function — compares rules with cart state, returns required actions.
   * No side effects, heavily tested in cart-drawer-tests.js.
   */
  calculateGiftActions(config, cart) {
    const actions = [];
    const cartByVariant = {};
    cart.items.forEach(item => {
      cartByVariant[item.variant_id] = item;
    });

    for (const rule of config) {
      const vid = parseInt(rule.giftVariantId);
      if (!vid) continue;

      const maxQty = parseInt(rule.maxQty) || 1;
      const minCents = parseInt(rule.minCartCents) || 0;
      const hasTag = rule.triggerTag && rule.triggerTag.length > 0;
      const hasAmount = minCents > 0;

      /* triggerActive comes from FRESH Liquid — it already evaluated tags */
      const tagMet = hasTag ? rule.triggerActive === true || rule.triggerActive === 'true' : false;
      const amountMet = hasAmount ? cart.total_price >= minCents : false;

      let shouldBeActive = false;
      if (hasTag && hasAmount) shouldBeActive = tagMet && amountMet;
      else if (hasTag) shouldBeActive = tagMet;
      else if (hasAmount) shouldBeActive = amountMet;

      /* Schedule gate. The window is server-only (lives in Liquid), so the
         client-side amount recompute above can't see it. An out-of-window rule
         must never be active regardless of tag/amount — this is what evicts a
         gift whose schedule has closed. Absent flag (legacy config) = open. */
      if (rule.inWindow === false || rule.inWindow === 'false') shouldBeActive = false;
/* Loyalty exclusivity gate — same server-only pattern as inWindow.
         When a Love Loyalty reward sits in the cart, every gift rule is
         hard-inactive regardless of tag/amount, which both evicts gifts
         already in the cart and blocks re-adding them. Absent flag
         (legacy config) = unlocked. */
      if (rule.loyaltyLock === true || rule.loyaltyLock === 'true') shouldBeActive = false;
      const item = cartByVariant[vid];
      const inCart = !!item;

      CartLogger.debug(`Rule: tag="${rule.triggerTag}" amount=${minCents}`, {
        tagMet, amountMet, shouldBeActive, inCart,
        currentQty: item?.quantity || 0
      });

      if (shouldBeActive && !inCart) {
        actions.push({ type: 'add', variantId: vid, quantity: 1 });
      } else if (!shouldBeActive && inCart) {
        actions.push({ type: 'remove', variantId: vid, lineKey: item.key });
      } else if (shouldBeActive && inCart && item.quantity > maxQty) {
        actions.push({ type: 'cap', variantId: vid, lineKey: item.key, quantity: maxQty });
      }
    }

    return actions;
  }

  setCheckoutLock(locked) {
    if (locked) {
      this.classList.add('eisNova-syncing');
      CartLogger.debug('Checkout: LOCKED');
    } else {
      /* Small delay before removing overlay for smooth visual transition */
      setTimeout(() => {
        this.classList.remove('eisNova-syncing');
        CartLogger.debug('Checkout: UNLOCKED');
      }, 300);
    }
  }

  /* =========================================
     SECTION: Discount

     /discount/CODE always returns 200 — it's a magic URL that sets a
     server-side pending discount, then the cart applies (or skips) it
     on next sync. So HTTP status tells us nothing.

     Authoritative success signal: cart savings increased.
       savings = original_total_price - total_price
     Works for BOTH cart-level codes (allocation_method='across') AND
     line-level codes (allocation_method='each'). Previous version checked
     cart_level_discount_applications count, which is empty for line-
     targeted codes — so success was sometimes mis-detected.

     refreshSections() runs ONLY on success. On failure we preserve the
     existing DOM so the inline error pill (errEl) stays visible with the
     user-typed code still in the input — they can correct and retry
     without losing context.
  ========================================= */
  async applyDiscount(code) {
    const trimmed = (code || '').trim();
    if (!trimmed) {
      return { success: false, message: 'Please enter a code' };
      /* LOYALTY EXCLUSIVITY: while a loyalty reward is in the cart, promo
       codes cannot be combined with it — reject before any network call. */
    if (this.isLoyaltyLocked()) {
      return { success: false, message: 'A loyalty reward is in your cart — promo codes cannot be combined with it.' };
    }
    }
    CartLogger.info('Applying discount:', trimmed);

    try {
      const before = await this.fetchCart();
      const beforeSavings = (before.original_total_price || 0) - (before.total_price || 0);

      await fetch(`/discount/${encodeURIComponent(trimmed)}`, { method: 'GET' });

      const after = await this.fetchCart();
      const afterSavings = (after.original_total_price || 0) - (after.total_price || 0);

      if (afterSavings > beforeSavings) {
        /* Refresh ONLY on success. New section HTML carries the breakdown rows
           and the strikethrough subtotal — the visual confirmation users need.
           refreshSections returns the sections map so we can piggyback gift
           sync without an additional roundtrip. */
        const sectionsMap = await this.refreshSections();
        await this._postDiscountGiftSync(sectionsMap);
        return { success: true };
      }
      return { success: false, message: 'Invalid or expired discount code' };
    } catch (err) {
      CartLogger.error('Discount failed:', err);
      return { success: false, message: 'Could not apply code. Please try again.' };
    }
  }

  /* =========================================
     LOYALTY EXCLUSIVITY
     A Love Loyalty reward in the cart is mutually exclusive with every
     other benefit. Liquid already kills gift rules (triggerActive:false,
     evicted by gift sync); this side handles discount codes.
  ========================================= */

  /* Reads the freshly-rendered section state — .drawer__inner is replaced
     on every render, so the attribute is always current. */
  isLoyaltyLocked() {
    return this.querySelector('[data-eisnova-loyalty-lock]')
      ?.getAttribute('data-eisnova-loyalty-lock') === 'true';
  }

  /*
    Clears any applied discount while a loyalty reward sits in the cart.
    - clearDiscount() is idempotent (savings===0 → skip), so the nested
      gift sync it triggers terminates instead of looping.
    - _loyaltyClearTried guards the one non-idempotent path: an admin
      "Automatic" discount cannot be removed from the storefront, so we
      attempt exactly once per lock period instead of every sync.
  */
  async _enforceLoyaltyExclusivity() {
    if (!this.isLoyaltyLocked()) {
      this._loyaltyClearTried = false; /* lock lifted — re-arm for next time */
      return;
    }
    if (this._loyaltyClearTried) return;
    this._loyaltyClearTried = true;
    CartLogger.info('Loyalty lock active: clearing other discounts');
    const result = await this.clearDiscount();
    if (result?.success) this._loyaltyClearTried = false; /* fully cleared — re-arm */
  }
  
  
  /*
    Discount removal is messy on Shopify. Three different scenarios, each
    needs a different signal to clear:

    1. CART-LEVEL ("across") code — sits in session cookie. /discount/clear
       removes the cookie, next cart fetch shows 0 savings. Fast path.

    2. LINE-LEVEL ("each") code — Shopify pre-allocates the discount onto
       line items at apply time. Clearing the session cookie alone does NOT
       retroactively wipe those allocations. The cart needs to be RE-EVALUATED
       to drop them. /cart/update.js with `discount: ''` forces that re-eval.
       This is the field's documented purpose in Hydrogen storefronts; it's
       just absent from official Cart Ajax API docs.

    3. AUTOMATIC discount — created in Admin as "Automatic" (not "Code").
       Server-authoritative: applied on every cart eval based on rules
       (cart contains tag X, customer in segment Y, etc.). Cannot be removed
       from storefront under any condition. We detect this case after
       attempts 1+2 fail to drop savings — savings come right back because
       Shopify re-applies during cart eval.

    Outcome reporting: returns { success, automatic? } so the click handler
    can show the right user-facing message.
  */
  async clearDiscount() {
    CartLogger.info('Clearing discount');
    const before = await this.fetchCart();
    const beforeSavings = (before.original_total_price || 0) - (before.total_price || 0);

    if (beforeSavings === 0) {
      CartLogger.debug('clearDiscount: nothing to clear, skipping');
      return { success: true };
    }

    try {
      /* Step 1: /cart/update with empty discount — forces allocation re-eval.
         This is the only path that handles line-level allocations correctly. */
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ discount: '' })
      });

      let after = await this.fetchCart();
      let afterSavings = (after.original_total_price || 0) - (after.total_price || 0);

      /* Step 2: if cart/update didn't take, try /discount/clear as legacy
         fallback. Some store configurations (older themes, custom apps)
         respond to /discount/clear but ignore the cart/update field. */
      if (afterSavings >= beforeSavings) {
        CartLogger.warn('clearDiscount: cart/update no-op, trying /discount/clear');
        await fetch('/discount/clear', { method: 'GET', credentials: 'same-origin' });
        after = await this.fetchCart();
        afterSavings = (after.original_total_price || 0) - (after.total_price || 0);
      }

      if (afterSavings >= beforeSavings) {
        /* Both paths failed. Discount is server-authoritative — most likely
           "Automatic discount" in Admin. Tell the caller so it can show a
           proper message instead of silent no-op. */
        CartLogger.error(
          'clearDiscount: discount cannot be removed from storefront. ' +
          'Most likely it is set as "Automatic discount" in Shopify Admin → ' +
          'Discounts (vs "Discount code"). These apply server-side and have ' +
          'no Storefront removal API.'
        );
        return { success: false, automatic: true };
      }

      /* Success — refresh DOM and re-evaluate gifts (total changed). */
      const sectionsMap = await this.refreshSections();
      await this._postDiscountGiftSync(sectionsMap);
      return { success: true };
    } catch (err) {
      CartLogger.error('clearDiscount failed:', err);
      return { success: false, error: err };
    }
  }

  /*
    Run gift sync after a discount mutation.

    Why this matters: gift_rule.triggerActive is computed in Liquid against
    cart.total_price, which is POST-discount. So applying a code that drops
    total below a 250€ threshold should evict the 250€-tier gift, and clearing
    a code that pushes total back above threshold should re-add the gift.

    Without this hook, gifts stay stale until the next quantity change
    triggers cartUpdate (which is when CartDrawerItems' subscriber fires
    runGiftSync). Users perceive this as "gifts disappear and don't come
    back" or "gift remained even though total dropped".

    Piggyback: refreshSections() just gave us the freshly rendered HTML,
    so we extract the new gift config from it instead of refetching.
  */
  async _postDiscountGiftSync(sectionsMap) {
    const drawerHTML = sectionsMap?.['cart-drawer'];
    const preloadedConfig = drawerHTML
      ? this.extractConfigFromSectionHTML(drawerHTML)
      : null;
    return this.runGiftSync({ preloadedConfig });
  }

  /* =========================================
     SECTION: Cart API Helpers
  ========================================= */
  fetchCart() {
    return fetch('/cart.js', {
      headers: { 'Content-Type': 'application/json' }
    }).then(r => r.json());
  }

  /**
   * POST to a cart mutation endpoint. Callers may include `sections`
   * in the body to have Shopify return fresh section HTML in the
   * response — this is the core of the piggyback optimization.
   */
  cartPost(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    }).then(r => {
      if (!r.ok) throw new Error(`${url}: ${r.status}`);
      return r.json();
    });
  }
}

customElements.define('cart-drawer', CartDrawer);


/* =========================================
   SECTION: CartDrawerItems
   After every quantity update, triggers gift sync — but now feeds
   the sync with the cart data that Dawn's CartItems just received,
   so the sync needs zero extra network round-trips for the first pass.
========================================= */
class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      { id: 'CartDrawer', section: 'cart-drawer', selector: '.drawer__inner' },
      { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' }
    ];
  }

  /**
   * CRITICAL: Lock checkout IMMEDIATELY when any quantity change begins.
   * This fires BEFORE the AJAX request, preventing checkout race conditions.
   */
  onChange(event) {
    document.querySelector('cart-drawer')?.setCheckoutLock(true);
    super.onChange(event);
  }

  /**
   * After Dawn's CartItems finishes updating, we piggyback on the
   * cart mutation response that was just published via pub/sub.
   * The response already contains the fresh section HTML with the
   * updated triggerActive, so we hand it to runGiftSync directly.
   */
  connectedCallback() {
    super.connectedCallback?.();

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this._giftSyncUnsub = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (event.source === 'gift-sync') return;
        CartLogger.debug('Cart updated, scheduling gift sync');
        clearTimeout(this._giftSyncTimer);

        /* 150ms is enough to coalesce rapid clicks without being
           perceived as lag. The previous 800ms was defensive padding
           that turned out to be unnecessary once we stopped doing
           our own parallel GETs right after the mutation. */
        this._giftSyncTimer = setTimeout(() => {
          const drawer = document.querySelector('cart-drawer');
          if (!drawer) return;
          drawer.rebindOverlay();

          /* Piggyback: Dawn's cart.js publishes the full parsed
             response (cartData) including the sections map.
             Extract fresh config from it — no extra GET needed. */
          const cartData = event.cartData;
          let preloadedConfig = null;
          let preloadedCart = null;

          if (cartData?.sections?.['cart-drawer']) {
            preloadedConfig = drawer.extractConfigFromSectionHTML(
              cartData.sections['cart-drawer']
            );
          }
          /* cartData from /cart/change.js is the full cart shape. */
          if (cartData && Array.isArray(cartData.items) &&
              typeof cartData.total_price === 'number') {
            preloadedCart = cartData;
          }

          drawer.runGiftSync({ preloadedConfig, preloadedCart });
        }, 150);
      });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    if (this._giftSyncUnsub) this._giftSyncUnsub();
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);


/* =========================================
   SECTION: Discount — clear handler only.
   The `submit` listener that used to live here was dead code: the form's
   apply button is type="button" so no form submit ever fires. The actual
   apply path is the click delegate in eisNova-overlay.js. Removing that
   listener prevents accidental double-fires if the button type ever
   changes back to "submit".
========================================= */
/*
  Discount remove (× button) — propagates the {success, automatic} outcome
  from clearDiscount() into the toast so users get explicit feedback when
  the discount can't be removed (automatic-discount edge case).
*/
document.addEventListener('click', e => {
  if (!e.target.closest('[data-discount-clear]')) return;
  e.preventDefault();
  const btn = e.target.closest('[data-discount-clear]');
  if (btn) btn.disabled = true;

  document.querySelector('cart-drawer')?.clearDiscount().then(result => {
    if (result?.automatic) {
      if (window.EisNovaToast) {
        window.EisNovaToast.show(
          'This discount is automatic and cannot be removed from cart. ' +
          'It will apply (or not) at checkout based on your cart contents.'
        );
      }
    } else if (result && !result.success) {
      if (window.EisNovaToast) {
        window.EisNovaToast.show('Could not remove discount. Please try again.');
      }
    }
    /* On success the cart re-renders and the × button gets a fresh DOM —
       no need to clear disabled state on the original (it's gone). */
  }).finally(() => {
    /* Defensive: if button still in DOM (which it shouldn't be on success
       because of refresh), restore. Find by query in case ref is stale. */
    document.querySelectorAll('[data-discount-clear][disabled]').forEach(b => {
      b.disabled = false;
    });
  });
});


/* =========================================
   SECTION: Cart Drawer Tests
   Run: CartTests.runAll() in browser console
========================================= */
window.CartTests = {
  async runAll() {
    console.log('%c=== Cart Drawer Tests ===', 'font-weight:bold;font-size:14px;color:#1a73e8');
    const results = [];
    const assert = (cond, name) => {
      results.push({ pass: cond, name });
      console.log(cond ? '  ✅' : '  ❌', name);
    };

    /* DOM Tests */
    console.group('DOM Structure');
    assert(!!document.querySelector('cart-drawer'), 'cart-drawer element exists');
    assert(!!document.querySelector('#CartDrawer'), 'CartDrawer wrapper exists');
    assert(!!document.querySelector('#CartDrawer-Overlay'), 'Overlay exists');
    assert(!!document.querySelector('.drawer__inner'), 'Inner panel exists');
    assert(!!document.querySelector('.drawer__header'), 'Header exists');
    assert(!!document.querySelector('.drawer__close'), 'Close button exists');
    assert(!!document.querySelector('#CartDrawer-Form'), 'Form exists');
    assert(!!document.querySelector('[data-checkout-btn]'), 'Checkout button exists');
    console.groupEnd();

    /* Component Tests */
    console.group('Web Components');
    assert(!!customElements.get('cart-drawer'), 'cart-drawer registered');
    assert(!!customElements.get('cart-drawer-items'), 'cart-drawer-items registered');
    assert(!!customElements.get('cart-items'), 'cart-items registered');
    assert(!!customElements.get('cart-remove-button'), 'cart-remove-button registered');
    assert(!!customElements.get('cart-note'), 'cart-note registered');

    const drawer = document.querySelector('cart-drawer');
    assert(typeof drawer.open === 'function', 'CartDrawer.open() exists');
    assert(typeof drawer.close === 'function', 'CartDrawer.close() exists');
    assert(typeof drawer.refreshAndOpen === 'function', 'CartDrawer.refreshAndOpen() exists');
    assert(typeof drawer.renderContents === 'function', 'CartDrawer.renderContents() exists');
    assert(typeof drawer.runGiftSync === 'function', 'CartDrawer.runGiftSync() exists');
    assert(typeof drawer.applyDiscount === 'function', 'CartDrawer.applyDiscount() exists');
    assert(typeof drawer.fetchFreshGiftConfig === 'function', 'CartDrawer.fetchFreshGiftConfig() exists');
    assert(typeof drawer.calculateGiftActions === 'function', 'CartDrawer.calculateGiftActions() exists');
    assert(typeof drawer.extractConfigFromSectionHTML === 'function', 'CartDrawer.extractConfigFromSectionHTML() exists');
    assert(typeof drawer.applySectionsFromMap === 'function', 'CartDrawer.applySectionsFromMap() exists');
    console.groupEnd();

    /* Gift Config Tests */
    console.group('Gift Config');
    const config = await drawer.fetchFreshGiftConfig();
    assert(config === null || Array.isArray(config), 'Gift config is null or array');
    if (config && config.length > 0) {
      config.forEach((rule, i) => {
        assert('triggerActive' in rule, `Rule ${i}: has triggerActive`);
        assert('giftVariantId' in rule, `Rule ${i}: has giftVariantId`);
        assert('maxQty' in rule, `Rule ${i}: has maxQty`);
        assert('minCartCents' in rule, `Rule ${i}: has minCartCents`);
        assert(!isNaN(parseInt(rule.giftVariantId)), `Rule ${i}: giftVariantId is numeric`);
      });
    } else {
      console.log('  ℹ️ No gift rules configured');
    }
    console.groupEnd();

    /* Cart API Tests */
    console.group('Cart API');
    try {
      const cart = await drawer.fetchCart();
      assert(typeof cart === 'object', 'fetchCart returns object');
      assert('items' in cart, 'Cart has items');
      assert('total_price' in cart, 'Cart has total_price');
      assert('item_count' in cart, 'Cart has item_count');
      console.log(`  ℹ️ Cart: ${cart.item_count} items, ${(cart.total_price/100).toFixed(2)} ${cart.currency}`);
    } catch (e) {
      assert(false, 'fetchCart works: ' + e.message);
    }
    console.groupEnd();

    /* Gift Action Calculator Tests */
    console.group('Gift Action Calculator');
    const mockCart = { items: [], total_price: 0, item_count: 0 };
    const mockRules = [
      { triggerTag: '', triggerActive: false, giftVariantId: '999', maxQty: 1, minCartCents: 10000 }
    ];

    let actions = drawer.calculateGiftActions(mockRules, mockCart);
    assert(actions.length === 0, 'No action when cart empty and threshold not met');

    const mockCart2 = { items: [], total_price: 15000, item_count: 2 };
    actions = drawer.calculateGiftActions(mockRules, mockCart2);
    assert(actions.length === 1 && actions[0].type === 'add', 'Add action when amount threshold met');

    const mockCart3 = {
      items: [{ variant_id: 999, key: 'abc', quantity: 1 }],
      total_price: 5000, item_count: 1
    };
    actions = drawer.calculateGiftActions(mockRules, mockCart3);
    assert(actions.length === 1 && actions[0].type === 'remove', 'Remove action when threshold no longer met');

    const mockRulesTag = [
      { triggerTag: 'VIP', triggerActive: true, giftVariantId: '888', maxQty: 1, minCartCents: 0 }
    ];
    actions = drawer.calculateGiftActions(mockRulesTag, { items: [], total_price: 0, item_count: 0 });
    assert(actions.length === 1 && actions[0].type === 'add', 'Add action when tag trigger is active');

    const mockRulesTag2 = [
      { triggerTag: 'VIP', triggerActive: false, giftVariantId: '888', maxQty: 1, minCartCents: 0 }
    ];
    const mockCart4 = { items: [{ variant_id: 888, key: 'def', quantity: 1 }], total_price: 0, item_count: 1 };
    actions = drawer.calculateGiftActions(mockRulesTag2, mockCart4);
    assert(actions.length === 1 && actions[0].type === 'remove', 'Remove when tag trigger deactivated');

    const mockRulesCap = [
      { triggerTag: 'VIP', triggerActive: true, giftVariantId: '777', maxQty: 1, minCartCents: 0 }
    ];
    const mockCart5 = { items: [{ variant_id: 777, key: 'ghi', quantity: 5 }], total_price: 0, item_count: 5 };
    actions = drawer.calculateGiftActions(mockRulesCap, mockCart5);
    assert(actions.length === 1 && actions[0].type === 'cap', 'Cap when qty exceeds max');
    console.groupEnd();

    /* Piggyback Helper Tests */
    console.group('Piggyback Helpers');
    const fakeHTML = `
      <html><body>
        <section class="shopify-section">
          <script id="CartDrawer-GiftConfig" type="application/json">
            [{"triggerTag":"TEST","triggerActive":true,"giftVariantId":"123","maxQty":1,"minCartCents":0}]
          </script>
        </section>
      </body></html>
    `;
    const extracted = drawer.extractConfigFromSectionHTML(fakeHTML);
    assert(Array.isArray(extracted) && extracted.length === 1, 'extractConfigFromSectionHTML parses valid HTML');
    assert(extracted[0].giftVariantId === '123', 'Extracted rule has correct variantId');
    assert(drawer.extractConfigFromSectionHTML('<html></html>') === null, 'Returns null when script tag missing');
    console.groupEnd();

    /* Open/Close Tests */
    console.group('Open / Close');
    drawer.open();
    await new Promise(r => setTimeout(r, 400));
    assert(drawer.classList.contains('active'), 'Drawer has .active after open');
    assert(document.body.classList.contains('overflow-hidden'), 'Body overflow hidden');

    drawer.close();
    await new Promise(r => setTimeout(r, 400));
    assert(!drawer.classList.contains('active'), 'Drawer loses .active after close');
    assert(!document.body.classList.contains('overflow-hidden'), 'Body overflow restored');
    console.groupEnd();

    /* Summary */
    const passed = results.filter(r => r.pass).length;
    const failed = results.filter(r => !r.pass).length;
    const color = failed === 0 ? '#2e7d32' : '#cc0000';
    console.log(`\n%c${passed}/${results.length} passed, ${failed} failed`,
      `font-weight:bold;font-size:14px;color:${color}`);

    if (failed > 0) {
      console.log('%cFailed:', 'color:#cc0000;font-weight:bold');
      results.filter(r => !r.pass).forEach(r => console.log('  ❌', r.name));
    }

    return { passed, failed, total: results.length };
  },

  /* Debug utilities */
  debug: {
    async state() {
      const cart = await fetch('/cart.js').then(r => r.json());
      console.table(cart.items.map(i => ({
        title: i.title, variant_id: i.variant_id, qty: i.quantity,
        price: (i.price/100).toFixed(2), key: i.key.substring(0, 20)
      })));
      console.log('Total:', (cart.total_price/100).toFixed(2), cart.currency);
    },

    async gifts() {
      const drawer = document.querySelector('cart-drawer');
      const config = await drawer.fetchFreshGiftConfig();
      const cart = await drawer.fetchCart();
      console.log('Fresh config:', config);
      console.log('Actions needed:', drawer.calculateGiftActions(config || [], cart));
    },

    verbose() { window.CART_DEBUG = true; console.log('Verbose logging ON'); },
    quiet() { window.CART_DEBUG = false; console.log('Verbose logging OFF'); }
  }
};
